import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { Pupil } from '@lyceo/homi/pupils/util-models';
import { AuthActions } from '@lyceo/shared/auth/data-access';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import {
  PupilAPIActions,
  PupilCoursesDialogActions,
  PupilProfileDialogActions,
} from './pupil.actions';
import { mapPupil } from './pupil.functions';

export const PUPIL_FEATURE_KEY: string = 'pupil';

export interface PupilState extends EntityState<Pupil> {
  profileCallState: CallState;
  getProfileCallState: CallState;
  coursesCallState: CallState;
}

export const pupilAdapter: EntityAdapter<Pupil> = createEntityAdapter<Pupil>();

export const initialPupilState: PupilState = pupilAdapter.getInitialState({
  profileCallState: LoadingState.INIT,
  getProfileCallState: LoadingState.INIT,
  coursesCallState: LoadingState.INIT,
});

const reducer: ActionReducer<PupilState, Action> = createReducer(
  initialPupilState,
  on(AuthActions.logoutSuccess, (): PupilState => initialPupilState),
  on(
    PupilProfileDialogActions.submitProfile,
    (state): PupilState => ({
      ...state,
      profileCallState: LoadingState.LOADING,
    }),
  ),
  on(
    PupilAPIActions.patchPupilProfileSuccess,
    (state, action): PupilState =>
      pupilAdapter.updateOne(
        {
          id: action.pupil.id,
          changes: action.pupil,
        },
        {
          ...state,
          profileCallState: LoadingState.LOADED,
        },
      ),
  ),
  on(
    PupilAPIActions.patchPupilProfileFailure,
    (state, { error }): PupilState => ({
      ...state,
      profileCallState: { errorMsg: error.message },
    }),
  ),
  on(
    PupilAPIActions.getPupilProfileById,
    (state): PupilState => ({
      ...state,
      getProfileCallState: LoadingState.LOADING,
    }),
  ),
  on(
    PupilAPIActions.getPupilProfileByIdSuccess,
    (state, action): PupilState =>
      pupilAdapter.upsertOne(mapPupil(action.pupilProfile), {
        ...state,
        getProfileCallState: LoadingState.LOADED,
      }),
  ),
  on(
    PupilAPIActions.getPupilProfileByIdFailure,
    (state, { error }): PupilState => ({
      ...state,
      getProfileCallState: { errorMsg: error.message },
    }),
  ),
  on(
    PupilCoursesDialogActions.submitCourses,
    (state): PupilState => ({
      ...state,
      coursesCallState: LoadingState.LOADING,
    }),
  ),
  on(
    PupilAPIActions.updatePupilCoursesSuccess,
    (state, action): PupilState =>
      pupilAdapter.updateOne(
        {
          id: action.pupilId,
          changes: { courseIds: action.courseIds },
        },
        {
          ...state,
          coursesCallState: LoadingState.LOADED,
        },
      ),
  ),
  on(
    PupilAPIActions.updatePupilCoursesFailure,
    (state, { error }): PupilState => ({
      ...state,
      coursesCallState: { errorMsg: error.message },
    }),
  ),
);

export function pupilReducer(
  state: PupilState | undefined,
  action: Action,
): PupilState {
  return reducer(state, action);
}
